import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Vendor, fetchVendor, getVendorGoogleData, fetchVendorImages } from '../../types/vendor.ts';
import { ArrowLeft, Star, MapPin, Phone, Mail, CheckCircle, Award, DollarSign, Calendar, ExternalLink, MessageSquare, Music, Car, Camera, Utensils, Home, Flower2, Gift, PartyPopper, Clock, Globe, Heart, X, Info, Users as UsersIcon, Share2 as ShareIcon, ChevronDown, ClipboardList, Clock3 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import QuoteRequestModal from './components/QuoteRequestModal.tsx';
import { QuoteRequestForm, QuoteRequestFormData } from '../../components/forms/QuoteRequestForm.tsx';
import { QuoteRequestAPI } from '../../services/api/quoteRequestApi.ts';

// Modern glassmorphic card component
const GlassCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={`backdrop-blur-lg bg-white/10 border border-white/20 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 ${className}`}
  >
    {children}
  </motion.div>
);

// Category-specific components with glassmorphic styling
const EntertainmentFeatures: React.FC<{ vendor: Vendor }> = ({ vendor }) => (
  <GlassCard className="p-8 bg-gradient-to-r from-[#FF4E8E]/5 to-[#8A2BE2]/5">
    <h3 className="text-2xl font-bold text-[#FF4E8E] mb-6">Music & Entertainment</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="backdrop-blur-md bg-white/5 p-6 rounded-xl border border-white/10">
        <div className="flex items-center gap-3 mb-4">
          <Music className="text-[#FF4E8E]" size={24} />
          <span className="font-medium text-gray-800">Sample Playlist</span>
        </div>
        <iframe 
          title="Spotify Sample Playlist"
          src="https://open.spotify.com/embed/playlist/37i9dQZF1DX8TSpgqViXCK" 
          width="100%" 
          height="152" 
          frameBorder="0" 
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
          loading="lazy"
          className="rounded-xl"
        />
      </div>
      <div className="space-y-4">
        <h4 className="font-medium text-gray-800">Equipment Setup</h4>
        <div className="grid gap-3">
          {[
            { icon: PartyPopper, text: 'Professional Sound System' },
            { icon: Music, text: 'Wireless Microphones' },
            { icon: Gift, text: 'LED Dance Floor' }
          ].map((item, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.02 }}
              className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10"
            >
              <item.icon size={20} className="text-[#FF4E8E]" />
              <span className="text-gray-700">{item.text}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </GlassCard>
);

const TransportFeatures: React.FC<{ vendor: Vendor }> = ({ vendor }) => (
  <GlassCard className="p-8 bg-gradient-to-r from-[#4169E1]/5 to-[#8A2BE2]/5">
    <h3 className="text-2xl font-bold text-[#4169E1] mb-6">Luxury Transport</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="relative h-48 rounded-xl overflow-hidden backdrop-blur-md bg-white/5 border border-white/10">
        <motion.div
          animate={{
            x: ["0%", "100%", "0%"],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <Car size={80} className="text-[#4169E1] opacity-20" />
        </motion.div>
      </div>
      <div className="space-y-4">
        <h4 className="font-medium text-gray-800">Fleet Details</h4>
        <div className="grid gap-3">
          {[
            'Luxury Sedans',
            'Vintage Cars',
            'Modern Limousines'
          ].map((item, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.02 }}
              className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10"
            >
              <Car size={20} className="text-[#4169E1]" />
              <span className="text-gray-700">{item}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </GlassCard>
);

const PhotographyFeatures: React.FC<{ vendor: Vendor }> = ({ vendor }) => (
  <GlassCard className="p-8 bg-gradient-to-r from-[#2DCEA8]/5 to-[#8A2BE2]/5">
    <h3 className="text-2xl font-bold text-[#2DCEA8] mb-6">Photography Style</h3>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <motion.div
          key={i}
          whileHover={{ scale: 1.05 }}
          className="relative aspect-square rounded-xl overflow-hidden backdrop-blur-md bg-white/5 border border-white/10"
        >
          <img 
            src={`/sample-photos/photo${i}.jpg`} 
            alt={`Style sample ${i}`}
            className="object-cover w-full h-full"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <Camera size={24} className="absolute bottom-4 right-4 text-white" />
        </motion.div>
      ))}
    </div>
  </GlassCard>
);

const CateringFeatures: React.FC<{ vendor: Vendor }> = ({ vendor }) => (
  <GlassCard className="p-8 bg-gradient-to-r from-[#FFB02E]/5 to-[#8A2BE2]/5">
    <h3 className="text-2xl font-bold text-[#FFB02E] mb-6">Culinary Experience</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <h4 className="font-medium text-gray-800">Menu Highlights</h4>
        <div className="grid gap-3">
          {['Appetizers', 'Main Course', 'Desserts'].map((course) => (
            <motion.div
              key={course}
              whileHover={{ scale: 1.02 }}
              className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10"
            >
              <Utensils size={20} className="text-[#FFB02E]" />
              <span className="text-gray-700">{course}</span>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="relative h-48 rounded-xl overflow-hidden backdrop-blur-md bg-white/5 border border-white/10">
        <motion.div
          animate={{
            rotate: [0, 360]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <Utensils size={80} className="text-[#FFB02E] opacity-20" />
        </motion.div>
      </div>
    </div>
  </GlassCard>
);

const VenueFeatures: React.FC<{ vendor: Vendor }> = ({ vendor }) => (
  <GlassCard className="p-8 bg-gradient-to-r from-[#5D5FEF]/5 to-[#8A2BE2]/5">
    <h3 className="text-2xl font-bold text-[#5D5FEF] mb-6">Venue Highlights</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <h4 className="font-medium text-gray-800">Venue Features</h4>
        <div className="grid gap-3">
          {[
            { icon: Home, text: `Capacity: ${vendor.capacity || '200'} guests` },
            { icon: MapPin, text: 'Indoor & Outdoor Spaces' },
            { icon: Calendar, text: 'All-Season Availability' }
          ].map((item, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.02 }}
              className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10"
            >
              <item.icon size={20} className="text-[#5D5FEF]" />
              <span className="text-gray-700">{item.text}</span>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="relative h-48 rounded-xl overflow-hidden backdrop-blur-md bg-white/5 border border-white/10">
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [1, 0.8, 1]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <Home size={80} className="text-[#5D5FEF] opacity-20" />
        </motion.div>
      </div>
    </div>
  </GlassCard>
);

const FloristFeatures: React.FC<{ vendor: Vendor }> = ({ vendor }) => (
  <GlassCard className="p-8 bg-gradient-to-r from-[#2DCEA8]/5 to-[#8A2BE2]/5">
    <h3 className="text-2xl font-bold text-[#2DCEA8] mb-6">Floral Design</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <h4 className="font-medium text-gray-800">Seasonal Specialties</h4>
        <div className="grid grid-cols-2 gap-3">
          {['Spring', 'Summer', 'Autumn', 'Winter'].map((season) => (
            <motion.div
              key={season}
              whileHover={{ scale: 1.05 }}
              className="p-4 rounded-xl bg-white/5 border border-white/10 text-center"
            >
              <Flower2 size={32} className="text-[#2DCEA8] mx-auto mb-2" />
              <span className="text-gray-700">{season}</span>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="relative h-48 rounded-xl overflow-hidden backdrop-blur-md bg-white/5 border border-white/10">
        <motion.div
          animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <Flower2 size={80} className="text-[#2DCEA8] opacity-20" />
        </motion.div>
      </div>
    </div>
  </GlassCard>
);

const PlannerFeatures: React.FC<{ vendor: Vendor }> = ({ vendor }) => (
  <GlassCard className="p-8 bg-gradient-to-r from-[#FFB02E]/5 to-[#8A2BE2]/5">
    <h3 className="text-2xl font-bold text-[#FFB02E] mb-6">Wedding Planning</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <h4 className="font-medium text-gray-800">Planning Highlights</h4>
        <div className="grid gap-3">
          {['Venue Selection', 'Vendor Coordination', 'Budget Management'].map((item, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.02 }}
              className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10"
            >
              <Info size={20} className="text-[#FFB02E]" />
              <span className="text-gray-700">{item}</span>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="relative h-48 rounded-xl overflow-hidden backdrop-blur-md bg-white/5 border border-white/10">
        <motion.div
          animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <Info size={80} className="text-[#FFB02E] opacity-20" />
        </motion.div>
      </div>
    </div>
  </GlassCard>
);

const getCategorySpecificFeatures = (vendor: Vendor) => {
  console.log('Vendor type:', vendor.vendorType);
  
  switch(vendor.vendorType) {
    case 'DJ':
    case 'ENTERTAINMENT':
      return <EntertainmentFeatures vendor={vendor} />;
    case 'TRANSPORT':
    case 'TRANSPORTATION':
      return <TransportFeatures vendor={vendor} />;
    case 'PHOTOGRAPHY':
    case 'PHOTOGRAPHERS':
      return <PhotographyFeatures vendor={vendor} />;
    case 'CATERING':
    case 'CATERERS':
      return <CateringFeatures vendor={vendor} />;
    case 'VENUES':
    case 'VENUE':
      return <VenueFeatures vendor={vendor} />;
    case 'FLORIST':
    case 'FLORISTS':
      return <FloristFeatures vendor={vendor} />;
    case 'WEDDING_PLANNER':
    case 'PLANNER':
      return <PlannerFeatures vendor={vendor} />;
    default:
      console.log('Unmatched vendor type:', vendor.vendorType);
      return null;
  }
};

const VendorDetailPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapCoordinates, setMapCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<QuoteRequestFormData>({
    firstName: '',
    lastName: '',
    weddingDate: '',
    isFlexible: false,
    vision: '',
    guestCount: 0,
    phone: ''
  });
  const [showMore, setShowMore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Add geocoding function
  const geocodeAddress = async (address: string) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`
      );
      const data = await response.json();
      
      if (data && data[0]) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  useEffect(() => {
    const loadVendor = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const foundVendor = await fetchVendor(Number(vendorId));

        if (foundVendor) {
          // Fetch vendor images
          const vendorImages = await fetchVendorImages(foundVendor.id);
          
          // Get Google Places data
          const { placeId, googlePlaceData } = await getVendorGoogleData(foundVendor);
          
          const vendorWithGoogleData = {
            ...foundVendor,
            placeId,
            googlePlaceData,
            vendorImages
          };
          
          setVendor(vendorWithGoogleData);
          
          // Geocode the vendor's address
          if (foundVendor.location || foundVendor.address) {
            const coords = await geocodeAddress(foundVendor.location || foundVendor.address || '');
            setMapCoordinates(coords);
          }
        } else {
          setError('Vendor not found.');
        }
      } catch (err) {
        console.error("❌ Error fetching vendor details:", err);
        setError('Failed to load vendor details.');
      } finally {
        setIsLoading(false);
      }
    };

    if (vendorId) {
      loadVendor();
    } else {
      setError('No vendor ID provided.');
      setIsLoading(false);
    }
  }, [vendorId]);

  const handleFormDataChange = (data: QuoteRequestFormData) => {
    setFormData(data);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!vendorId || !vendor) {
      setError('No vendor information available.');
      return;
    }

    setIsSubmitting(true);
    setShowSuccess(false);
    setError(null);
    
    try {
      const response = await QuoteRequestAPI.sendQuoteRequest(
        formData,
        vendorId,
        vendor.email || ''
      );

      if (response.success) {
        setShowSuccess(true);
        setTimeout(() => {
          setFormData({
            firstName: '',
            lastName: '',
            weddingDate: '',
            isFlexible: false,
            vision: '',
            guestCount: 0,
            phone: ''
          });
          setShowSuccess(false);
        }, 2000);
      } else {
        setError(response.error || 'Failed to submit quote request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('Failed to submit quote request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !vendor) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50/30 via-pink-50/20 to-orange-50/30 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-[#FF4E8E] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50/30 via-pink-50/20 to-orange-50/30 flex flex-col items-center justify-center p-4">
        <GlassCard className="p-8 text-center max-w-md">
          <p className="text-xl text-red-500 mb-4">{error}</p>
          <Link
            to="/connect"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FF4E8E] to-[#FF6B6B] text-white rounded-xl hover:shadow-lg transition-all"
          >
            <ArrowLeft size={20} />
            Back to Connect
          </Link>
        </GlassCard>
      </div>
    );
  }

  const sortedImages = vendor.vendorImages
    ? [...vendor.vendorImages].sort((a, b) => a.displayOrder - b.displayOrder)
    : [];

  const mainImage = sortedImages[0];
  const sideImages = sortedImages.slice(1, 5);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50/30 via-pink-50/20 to-orange-50/30">
      <AnimatePresence>
        {isModalOpen && (
          <QuoteRequestModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            vendor={vendor}
          />
        )}
      </AnimatePresence>

      {/* Modern Navigation */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-white/10 border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-gray-600 hover:text-[#FF4E8E] transition-colors">Home</Link>
              <span className="text-gray-400">/</span>
              <Link to="/connect" className="text-gray-600 hover:text-[#FF4E8E] transition-colors">Vendors</Link>
              <span className="text-gray-400">/</span>
              <span className="text-[#FF4E8E] font-medium">{vendor.name}</span>
        </nav>
            <div className="flex items-center gap-2">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-2 rounded-xl backdrop-blur-md bg-white/20 border border-white/30 text-gray-700 hover:bg-white/30 transition-all"
              >
                <ShareIcon size={18} />
              </motion.button>
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-2 rounded-xl backdrop-blur-md bg-white/20 border border-white/30 text-gray-700 hover:bg-white/30 transition-all"
              >
                <Heart size={18} />
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Hero Image Grid - TheKnot/WeddingWire style */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-5 gap-4 mb-8"
        >
        {/* Main Image */}
          <div className="col-span-5 md:col-span-3 h-[400px] lg:h-[500px]">
            {mainImage ? (
              <img 
                src={mainImage.s3Url} 
                alt={vendor.name} 
                className="w-full h-full object-cover rounded-3xl shadow-xl" 
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 rounded-3xl flex items-center justify-center">
                <Camera size={60} className="text-gray-400" />
              </div>
          )}
        </div>
        {/* Side Images */}
          <div className="hidden md:grid col-span-2 grid-cols-2 grid-rows-2 gap-2 h-[400px] lg:h-[500px]">
            {sideImages.length > 0 ? sideImages.map((img, i) => (
              <img 
                key={img.id} 
                src={img.s3Url} 
                alt={`${vendor.name} photo ${i + 2}`} 
                className="w-full h-full object-cover rounded-2xl shadow-lg" 
              />
            )) : (
              [...Array(4)].map((_, i) => (
                <div 
                  key={i}
                  className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center"
                >
                  <Camera size={30} className="text-gray-400" />
        </div>
              ))
            )}
      </div>
        </motion.div>

        {/* Vendor Header Card */}
        <GlassCard className="p-6 mb-8 bg-gradient-to-r from-white/20 via-white/10 to-purple-50/20">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-800 to-[#FF4E8E] bg-clip-text text-transparent">
                  {vendor.name}
                </h1>
              <div className="flex gap-2">
                {vendor.verified && (
                    <span className="px-3 py-1 rounded-full bg-emerald-100/80 text-emerald-700 text-sm font-medium flex items-center gap-1.5">
                    <CheckCircle size={14} />
                    Verified
                  </span>
                )}
                {vendor.topVendor && (
                    <span className="px-3 py-1 rounded-full bg-amber-100/80 text-amber-700 text-sm font-medium flex items-center gap-1.5">
                    <Award size={14} />
                    Top Vendor
                  </span>
                )}
              </div>
            </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-xl backdrop-blur-md bg-white/10 border border-white/20">
                  <MapPin size={16} className="text-[#FF4E8E]" />
                  <span className="text-gray-700 text-sm">{vendor.googlePlaceData?.formatted_address || vendor.address}</span>
              </div>
                <div className="flex items-center gap-3 p-3 rounded-xl backdrop-blur-md bg-white/10 border border-white/20">
                  <Star size={16} className="text-amber-500 fill-amber-500" />
                  <span className="text-gray-700 text-sm">
                    {vendor.googlePlaceData?.rating?.toFixed(1) || '5.0'} ({vendor.googlePlaceData?.user_ratings_total || 0})
                  </span>
              </div>
                <div className="flex items-center gap-3 p-3 rounded-xl backdrop-blur-md bg-white/10 border border-white/20">
                  <DollarSign size={16} className="text-emerald-500" />
                  <span className="text-gray-700 text-sm">
                    {vendor.approximateCharge ? `$${vendor.approximateCharge.toLocaleString()}` : 'Quote Available'}
                  </span>
              </div>
                <div className="flex items-center gap-3 p-3 rounded-xl backdrop-blur-md bg-white/10 border border-white/20">
                  <UsersIcon size={16} className="text-blue-500" />
                  <span className="text-gray-700 text-sm">{vendor.capacity || '—'} guests</span>
              </div>
            </div>
          </div>
            
          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#FF4E8E] to-[#FF6B6B] text-white font-medium shadow-xl hover:shadow-2xl transition-all flex items-center gap-2"
              onClick={() => setIsModalOpen(true)}
            >
                <MessageSquare size={18} />
              Request Quote
            </motion.button>
          </div>
            </div>
          </GlassCard>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* About Section */}
            <GlassCard className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-1 h-12 bg-gradient-to-b from-[#FF4E8E] to-[#FF6B6B] rounded-full" />
                <h2 className="text-2xl font-bold text-gray-800">About {vendor.name}</h2>
                  </div>
              <p className="text-gray-700 leading-relaxed text-lg">
                {vendor.bio || 'Professional event planning and vendor services for your perfect wedding day.'}
              </p>
            </GlassCard>

            {/* Services Section */}
            {vendor.services && vendor.services.length > 0 && (
              <GlassCard className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-1 h-12 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full" />
                  <h2 className="text-2xl font-bold text-gray-800">Services</h2>
                </div>
                <div className="grid gap-6">
                  {vendor.services.map((service, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.01 }}
                      className="p-6 rounded-2xl backdrop-blur-md bg-white/5 border border-white/10"
                    >
                      <h3 className="text-xl font-semibold text-gray-800 mb-4 capitalize">
                          {service.serviceType}
                        </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {service.serviceDescriptions?.map((desc, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10"
                          >
                            <div className="w-2 h-2 rounded-full bg-[#FF4E8E]" />
                            <span className="text-gray-700">{desc}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </GlassCard>
            )}

            {/* Photo Gallery */}
            <GlassCard className="p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-1 h-12 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full" />
                <h2 className="text-2xl font-bold text-gray-800">Photo Gallery</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {sortedImages.length > 0 ? sortedImages.map((image, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.05 }}
                    className="relative aspect-square rounded-2xl overflow-hidden shadow-lg"
                  >
                    <img
                      src={image.s3Url}
                      alt={`${vendor.name} photo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                )) : (
                  <div className="col-span-full text-center py-12">
                    <Camera size={48} className="text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Photo gallery coming soon</p>
                  </div>
                )}
              </div>
            </GlassCard>

            {/* Reviews Section */}
            {vendor.googlePlaceData?.reviews && vendor.googlePlaceData.reviews.length > 0 && (
              <GlassCard className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-1 h-12 bg-gradient-to-b from-amber-500 to-orange-500 rounded-full" />
                  <h2 className="text-2xl font-bold text-gray-800">Reviews</h2>
                </div>
                
                <div className="bg-white/10 rounded-2xl p-6 mb-6">
                  <div className="flex items-center gap-6">
                    <div className="text-4xl font-bold text-[#FF4E8E]">
                      {vendor.googlePlaceData?.rating?.toFixed(1) || '5.0'}
                    </div>
                    <div>
                      <div className="flex gap-1 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={20}
                            className={i < Math.round(vendor.googlePlaceData?.rating || 5) 
                              ? "text-amber-500 fill-amber-500" 
                              : "text-gray-300"
                            }
                          />
                        ))}
                      </div>
                      <p className="text-gray-600">
                        Based on {vendor.googlePlaceData?.user_ratings_total || 0} reviews
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {vendor.googlePlaceData.reviews.slice(0, 3).map((review, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="p-6 rounded-2xl bg-white/5 border border-white/10"
                    >
                      <div className="flex items-start gap-4">
                        <img
                          src={review.profile_photo_url}
                          alt={review.author_name}
                          className="w-12 h-12 rounded-full border-2 border-white/20"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-gray-800">{review.author_name}</h4>
                            <span className="text-sm text-gray-500">{review.relative_time_description}</span>
                          </div>
                          <div className="flex gap-1 mb-2">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                size={14}
                                className={i < review.rating 
                                  ? "text-amber-500 fill-amber-500" 
                                  : "text-gray-300"
                                }
                              />
                            ))}
                          </div>
                          <p className="text-gray-600">{review.text}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </GlassCard>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quote Request Form */}
            <GlassCard className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Get a Quote</h3>
              
                {showSuccess && (
                <div className="mb-4 p-3 rounded-xl bg-emerald-100/20 border border-emerald-200/30 flex items-center gap-2">
                  <CheckCircle size={16} className="text-emerald-600" />
                  <p className="text-emerald-700 text-sm">Quote request sent successfully!</p>
                  </div>
                )}

                <QuoteRequestForm
                  formData={formData}
                onFormDataChange={handleFormDataChange}
                  onSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                  variant="inline"
                className="space-y-4"
                />
            </GlassCard>

            {/* Contact Information */}
            <GlassCard className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Contact</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                  <MapPin size={18} className="text-[#FF4E8E] mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-800">Location</h4>
                    <p className="text-gray-600 text-sm">{vendor.googlePlaceData?.formatted_address || vendor.address}</p>
                  </div>
                </div>
                {vendor.phone && (
                  <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                    <Phone size={18} className="text-[#FF4E8E]" />
                    <a href={`tel:${vendor.phone}`} className="text-gray-600 hover:text-[#FF4E8E] transition-colors">
                      {vendor.phone}
                    </a>
                  </div>
                )}
                {vendor.googlePlaceData?.website && (
                  <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                    <Globe size={18} className="text-[#FF4E8E]" />
                    <a
                      href={vendor.googlePlaceData.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-[#FF4E8E] transition-colors"
                    >
                      Visit Website
                    </a>
                  </div>
                )}
              </div>
            </GlassCard>

            {/* Business Hours */}
            {vendor.googlePlaceData?.opening_hours?.weekday_text && (
              <GlassCard className="p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Hours</h3>
                <div className="space-y-2">
                  {vendor.googlePlaceData.opening_hours.weekday_text.map((day, index) => (
                    <div
                      key={index}
                      className={`flex justify-between py-2 text-sm ${
                        index === new Date().getDay() - 1
                          ? 'text-[#FF4E8E] font-medium bg-pink-50/20 px-3 rounded-lg' 
                          : 'text-gray-600'
                      }`}
                    >
                      <span>{day.split(': ')[0]}</span>
                      <span>{day.split(': ')[1]}</span>
                    </div>
                  ))}
                </div>
              </GlassCard>
            )}

            {/* Map */}
            <GlassCard className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Location</h3>
              <div className="relative h-64 rounded-2xl overflow-hidden">
                {mapCoordinates ? (
                  <iframe
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapCoordinates.lng - 0.01},${mapCoordinates.lat - 0.01},${mapCoordinates.lng + 0.01},${mapCoordinates.lat + 0.01}&layer=mapnik&marker=${mapCoordinates.lat},${mapCoordinates.lng}&zoom=15`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    className="rounded-2xl"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-white/10">
                    <div className="text-center">
                      <MapPin size={40} className="text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">Map loading...</p>
                    </div>
                  </div>
                )}
              </div>
              {mapCoordinates && (
                  <motion.a
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    href={`https://www.google.com/maps/search/?api=1&query=${mapCoordinates.lat},${mapCoordinates.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  className="mt-4 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FF4E8E] to-[#FF6B6B] text-white rounded-xl hover:shadow-lg transition-all text-sm"
                  >
                    <ExternalLink size={14} />
                    Open in Google Maps
                  </motion.a>
              )}
            </GlassCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDetailPage; 
